import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  User,
  FileText,
  Briefcase,
  Mail,
  Moon,
  Sun,
  Github,
  Linkedin,
  Twitter,
  Instagram,
  ExternalLink,
  Rocket,
  Brain
} from 'lucide-react';
// import joseph from '../../jo-cool.jpeg';
import joseph from '../../image.png';
import { personalInfo } from '../../data/personalInfo';

const Sidebar = ({ value: { lines, setLines, darkMode, toggleDarkMode } }) => {
  const [activeSection, setActiveSection] = useState('hero');

  const navigationItems = useMemo(() => [
    { id: 'hero', label: 'Home', icon: Home },
    { id: 'about', label: 'About', icon: User },
    { id: 'neural-network', label: 'AI Skills', icon: Brain },
    { id: 'experience', label: 'Experience', icon: FileText },
    { id: 'portfolio', label: 'Portfolio', icon: Briefcase },
    { id: 'contact', label: 'Contact', icon: Mail },
  ], []);

  const socialLinks = [
    {
      icon: Github,
      url: personalInfo.social.github,
      label: 'GitHub',
      color: 'hover:text-gray-900 dark:hover:text-white'
    },
    {
      icon: Linkedin,
      url: personalInfo.social.linkedin,
      label: 'LinkedIn',
      color: 'hover:text-blue-500'
    },
    {
      icon: Twitter,
      url: 'https://twitter.com/JosephOfili3',
      label: 'Twitter',
      color: 'hover:text-blue-400'
    },
    {
      icon: Instagram,
      url: 'https://www.instagram.com/ofilijoseph/',
      label: 'Instagram',
      color: 'hover:text-pink-500'
    },
  ];

  // Track active section based on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = navigationItems.map(item => item.id);
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [navigationItems]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    // Close mobile menu after navigation
    if (lines) {
      setLines();
    }
  };

  return (
    <AnimatePresence>
      {/* Desktop Sidebar */}
      <motion.aside
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          height: '100%',
          width: '320px',
          background: darkMode ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(12px)',
          borderRight: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
          zIndex: 40,
          transform: lines ? (window.innerWidth >= 1024 ? 'translateX(0)' : 'translateX(-100%)') : 'translateX(0)',
          transition: 'transform 0.3s ease',
          display: window.innerWidth >= 1024 ? 'block' : 'none'
        }}
        initial={{ x: -320 }}
        animate={{ x: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          padding: '24px',
          overflow: 'hidden'
        }}>
          {/* Profile Section */}
          <motion.div
            style={{
              textAlign: 'center',
              marginBottom: '32px'
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              style={{
                position: 'relative',
                width: '128px',
                height: '128px',
                margin: '0 auto 16px'
              }}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            >
              <img
                src={joseph}
                alt="Joseph Ofili"
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '4px solid #0ea5e9',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)'
                }}
              />
              <div style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.2), rgba(168, 85, 247, 0.2))'
              }}></div>
            </motion.div>

            <h2 style={{
              fontSize: '1.25rem',
              fontWeight: 'bold',
              color: darkMode ? 'white' : '#111827',
              marginBottom: '8px'
            }}>
              {personalInfo.name}
            </h2>
            <p style={{
              fontSize: '0.875rem',
              color: darkMode ? '#9ca3af' : '#6b7280',
              marginBottom: '16px'
            }}>
              {personalInfo.title}
            </p>
            <div style={{
              fontSize: '0.75rem',
              color: darkMode ? '#6b7280' : '#9ca3af'
            }}>
              📍 {personalInfo.location}
            </div>
          </motion.div>

          {/* FlowForge Project Highlight */}
          <motion.div
            style={{
              background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.1), rgba(14, 165, 233, 0.1))',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '32px',
              border: '1px solid rgba(168, 85, 247, 0.2)'
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '8px'
            }}>
              <Rocket size={16} style={{ color: '#a855f7' }} />
              <span style={{
                fontSize: '0.75rem',
                fontWeight: 'bold',
                color: '#a855f7',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Personal Project
              </span>
            </div>
            <h4 style={{
              fontSize: '1rem',
              fontWeight: 'bold',
              color: darkMode ? 'white' : '#111827',
              marginBottom: '4px'
            }}>
              FlowForge
            </h4>
            <p style={{
              fontSize: '0.75rem',
              color: darkMode ? '#9ca3af' : '#6b7280',
              lineHeight: '1.4',
              marginBottom: '12px'
            }}>
              AI-powered automation platform that eliminates repetitive web tasks for businesses
            </p>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '4px'
            }}>
              <span style={{
                fontSize: '0.625rem',
                background: 'rgba(168, 85, 247, 0.2)',
                color: '#a855f7',
                padding: '2px 6px',
                borderRadius: '8px'
              }}>
                AI/ML
              </span>
              <span style={{
                fontSize: '0.625rem',
                background: 'rgba(14, 165, 233, 0.2)',
                color: '#0ea5e9',
                padding: '2px 6px',
                borderRadius: '8px'
              }}>
                Automation
              </span>
              <span style={{
                fontSize: '0.625rem',
                background: 'rgba(52, 211, 153, 0.2)',
                color: '#34d399',
                padding: '2px 6px',
                borderRadius: '8px'
              }}>
                SaaS
              </span>
            </div>
          </motion.div>

          {/* Navigation */}
          <nav style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingRight: '4px',
            scrollbarWidth: 'thin',
            scrollbarColor: darkMode ? '#374151 transparent' : '#e5e7eb transparent'
          }}>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              {navigationItems.map((item, index) => {
                const IconComponent = item.icon;
                const isActive = activeSection === item.id;

                return (
                  <motion.li
                    key={item.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <button
                      onClick={() => scrollToSection(item.id)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        transition: 'all 0.3s ease',
                        textAlign: 'left',
                        border: 'none',
                        cursor: 'pointer',
                        background: isActive ? '#0ea5e9' : 'transparent',
                        color: isActive ? 'white' : (darkMode ? '#d1d5db' : '#374151'),
                        boxShadow: isActive ? '0 4px 12px rgba(14, 165, 233, 0.3)' : 'none'
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.target.style.background = darkMode ? '#1e293b' : '#f3f4f6';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.target.style.background = 'transparent';
                        }
                      }}
                    >
                      <IconComponent size={20} />
                      <span style={{ fontWeight: '500' }}>{item.label}</span>
                    </button>
                  </motion.li>
                );
              })}
            </ul>
          </nav>

          {/* Social Links */}
          <motion.div
            style={{ marginTop: '32px' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '12px',
              marginBottom: '24px'
            }}>
              {socialLinks.map((social, index) => {
                const IconComponent = social.icon;
                return (
                  <motion.a
                    key={social.label}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '8px',
                      borderRadius: '8px',
                      background: darkMode ? '#1e293b' : '#f3f4f6',
                      color: darkMode ? '#9ca3af' : '#6b7280',
                      transition: 'all 0.3s ease',
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.9 + index * 0.1 }}
                  >
                    <IconComponent size={18} />
                  </motion.a>
                );
              })}
            </div>

            {/* Theme Toggle */}
            <motion.button
              onClick={toggleDarkMode}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '8px 16px',
                borderRadius: '8px',
                background: darkMode ? '#1e293b' : '#f3f4f6',
                color: darkMode ? '#d1d5db' : '#374151',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {darkMode ? <Sun size={18} /> : <Moon size={18} />}
              <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                {darkMode ? 'Light Mode' : 'Dark Mode'}
              </span>
            </motion.button>

            {/* Resume Link */}
            <motion.a
              href={personalInfo.social.resume}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '8px 16px',
                marginTop: '12px',
                borderRadius: '8px',
                background: '#0ea5e9',
                color: 'white',
                textDecoration: 'none',
                transition: 'all 0.3s ease'
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <ExternalLink size={18} />
              <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>View Resume</span>
            </motion.a>
          </motion.div>
        </div>
      </motion.aside>

      {/* Mobile Sidebar Overlay */}
      {lines && (
        <motion.div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 30,
            display: window.innerWidth >= 1024 ? 'none' : 'block'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={setLines}
        />
      )}

      {/* Mobile Sidebar */}
      <motion.aside
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          height: '100%',
          width: '320px',
          background: darkMode ? '#0f172a' : 'white',
          zIndex: 40,
          transform: lines ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.3s ease',
          display: window.innerWidth >= 1024 ? 'none' : 'block'
        }}
        initial={{ x: -320 }}
        animate={{ x: lines ? 0 : -320 }}
        transition={{ duration: 0.3 }}
      >
        {/* Mobile sidebar content - simplified version */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          padding: '24px',
          overflow: 'hidden'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <img
              src={joseph}
              alt="Joseph Ofili"
              style={{
                width: '96px',
                height: '96px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '4px solid #0ea5e9',
                margin: '0 auto 16px'
              }}
            />
            <h2 style={{
              fontSize: '1.125rem',
              fontWeight: 'bold',
              color: darkMode ? 'white' : '#111827',
              marginBottom: '8px'
            }}>
              {personalInfo.name}
            </h2>
            <p style={{
              fontSize: '0.875rem',
              color: darkMode ? '#9ca3af' : '#6b7280'
            }}>
              {personalInfo.title}
            </p>
          </div>

          <nav style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingRight: '4px',
            scrollbarWidth: 'thin',
            scrollbarColor: darkMode ? '#374151 transparent' : '#e5e7eb transparent'
          }}>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {navigationItems.map((item) => {
                const IconComponent = item.icon;
                const isActive = activeSection === item.id;

                return (
                  <li key={item.id} style={{ marginBottom: '8px' }}>
                    <button
                      onClick={() => scrollToSection(item.id)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        cursor: 'pointer',
                        background: isActive ? '#0ea5e9' : 'transparent',
                        color: isActive ? 'white' : (darkMode ? '#d1d5db' : '#374151'),
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <IconComponent size={20} />
                      <span style={{ fontWeight: '500' }}>{item.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </motion.aside>
    </AnimatePresence>
  );
};

export default Sidebar;